import Swiper from 'swiper';
import { Autoplay, Navigation, EffectFade, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import 'swiper/css/mousewheel';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// common variables
const mql = window.matchMedia('(min-width: 768px)');
const mqlLg = window.matchMedia('(min-width: 1024px)');
const mqlXl = window.matchMedia('(min-width: 1280px)');

// reload on resize
mql.onchange = () => {
  location.reload();
};

mqlLg.onchange = () => {
  location.reload();
};

const kvSwiper = document.querySelector('.js-kv-swiper');

if (kvSwiper) {
  const swiper = new Swiper('.js-kv-swiper', {
    modules: [Autoplay],
    init: false,
    loop: true,
    speed: 10000,
    autoplay: {
      delay: 0,
      disableOnInteraction: false,
    },
    slidesPerView: 1.3,
    spaceBetween: 16,
    slidesOffsetBefore: 16,
    slidesOffsetAfter: 16,
    allowTouchMove: false,

    breakpoints: {
      768: {
        slidesPerView: 2.5,
        spaceBetween: 16,
        slidesOffsetBefore: 16,
        slidesOffsetAfter: 16,
      },
      1024: {
        slidesPerView: 'auto',
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
    },
  });
  swiper.init();
}

const topInterviewSwiper = document.querySelector(
  '.js-top-interview-swiper'
) as HTMLElement;
if (topInterviewSwiper) {
  let swiper: Swiper | null = null;

  const checkBreakpoint = (e: MediaQueryListEvent) => {
    if (!e.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const checkInitialBreakpoint = () => {
    if (!mql.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const initSwiper = () => {
    swiper = new Swiper(topInterviewSwiper, {
      modules: [Navigation],
      init: false,
      loop: false,
      slidesPerView: 1.2,
      spaceBetween: 0,

      allowTouchMove: true,

      navigation: {
        nextEl: '.js-top-interview-swiper-next',
        prevEl: '.js-top-interview-swiper-prev',
      },

      breakpoints: {
        768: {
          slidesPerView: 1,
          spaceBetween: 0,
        },
      },
    });

    swiper.init();
  };

  mql.addEventListener('change', checkBreakpoint);
  checkInitialBreakpoint();
}

const topStorySwiper = document.querySelector(
  '.js-top-story-swiper'
) as HTMLElement;
if (topStorySwiper) {
  let swiper: Swiper | null = null;

  const checkBreakpoint = (e: MediaQueryListEvent) => {
    if (!e.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const checkInitialBreakpoint = () => {
    if (!mql.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const initSwiper = () => {
    swiper = new Swiper(topStorySwiper, {
      modules: [Autoplay],
      init: false,
      loop: true,
      slidesPerView: 1.3,
      spaceBetween: 8,
      speed: 7000,
      autoplay: {
        delay: 0,
        disableOnInteraction: false,
      },

      allowTouchMove: false,

      breakpoints: {
        768: {
          slidesPerView: 1,
          spaceBetween: 0,
        },
      },
    });

    swiper.init();
  };

  mql.addEventListener('change', checkBreakpoint);
  checkInitialBreakpoint();
}

const topMemberSwiper = document.querySelector(
  '.js-top-member-swiper'
) as HTMLElement;

if (topMemberSwiper) {
  const swiper = new Swiper(topMemberSwiper, {
    modules: [Autoplay],
    init: false,
    loop: true,
    slidesPerView: 1.3,
    spaceBetween: 16,
    speed: 10000,
    autoplay: {
      delay: 0,
      disableOnInteraction: false,
    },

    allowTouchMove: false,

    breakpoints: {
      768: {
        slidesPerView: 3.6,
        spaceBetween: 24,
      },
    },
  });
  swiper.init();
}

const details = document.querySelectorAll('.js-details');
const IS_OPENED_CLASS = 'is-opened';

const nav = document.getElementById('nav');
const navButton = document.getElementById('nav-button');
const navCloseButtons = document.querySelectorAll('.js-nav-close-button');

if (navButton && nav) {
  navButton.addEventListener('click', () => {
    navButton.classList.toggle('is-opened');
    nav.classList.toggle('is-opened');
  });

  navCloseButtons.forEach((button) => {
    button.addEventListener('click', () => {
      navButton.classList.remove('is-opened');
      nav.classList.remove('is-opened');
    });
  });
}

details.forEach((element) => {
  const summary = element.querySelector('.js-summary');
  const content = element.querySelector('.js-content');

  if (summary) {
    summary.addEventListener('click', (event) => {
      event.preventDefault();

      if (element.classList.contains(IS_OPENED_CLASS)) {
        element.classList.toggle(IS_OPENED_CLASS);

        closingAnim(content, element).restart();
      } else {
        element.classList.toggle(IS_OPENED_CLASS);

        element.setAttribute('open', 'true');

        openingAnim(content).restart();
      }
    });
  }
});

const closingAnim = (content: gsap.TweenTarget, element: Element) =>
  gsap.to(content, {
    height: 0,
    duration: 0.4,
    ease: 'power3.out',
    overwrite: true,
    onComplete: () => {
      element.removeAttribute('open');
    },
  });

const openingAnim = (content: gsap.TweenTarget) =>
  gsap.fromTo(
    content,
    {
      height: 0,
    },
    {
      height: 'auto',
      duration: 0.4,
      ease: 'power3.out',
      overwrite: true,
    }
  );

// fixed viewport 320px
const viewport = document.querySelector('meta[name="viewport"]');
function switchViewport() {
  const value =
    window.outerWidth > 320
      ? 'width=device-width,initial-scale=1'
      : 'width=320';
  if (viewport && viewport.getAttribute('content') !== value) {
    viewport.setAttribute('content', value);
  }
}
addEventListener('resize', switchViewport, false);
switchViewport();

// scroll animation trigger
const scrollAnimationTrigger = () => {
  const callback: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        const target = entry.target;
        target.classList.add('is-animated');
      }
    });
  };

  const options = {
    rootMargin: '0px 0px -25%',
  };

  const observer = new IntersectionObserver(callback, options);

  const targets = document.querySelectorAll('[data-animation-trigger]');
  targets.forEach((target) => {
    observer.observe(target);
  });
};
scrollAnimationTrigger();

// member ocuupations
const interviewSwiper = document.querySelector(
  '.js-interview-swiper'
) as HTMLElement;

if (interviewSwiper) {
  let swiper: Swiper | null = null;

  const checkBreakpoint = (e: MediaQueryListEvent) => {
    if (!e.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const checkInitialBreakpoint = () => {
    if (!mqlXl.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const initSwiper = () => {
    swiper = new Swiper(interviewSwiper, {
      modules: [Navigation],
      init: false,
      loop: false,
      slidesPerView: 1.2,
      spaceBetween: 0,

      navigation: {
        nextEl: '.js-interview-swiper-next',
        prevEl: '.js-interview-swiper-prev',
      },

      breakpoints: {
        768: {
          slidesPerView: 2.3,
          spaceBetween: 0,
        },
      },
    });

    swiper.init();
  };

  mqlXl.addEventListener('change', checkBreakpoint);
  checkInitialBreakpoint();
}

const memberSwiper = document.querySelector('.js-member-swiper') as HTMLElement;
if (memberSwiper) {
  let swiper: Swiper | null = null;

  const checkBreakpoint = (e: MediaQueryListEvent) => {
    if (!e.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const checkInitialBreakpoint = () => {
    if (!mql.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const initSwiper = () => {
    swiper = new Swiper(memberSwiper, {
      init: false,
      loop: true,
      slidesPerView: 1.3,
      spaceBetween: 16,

      breakpoints: {
        768: {
          slidesPerView: 3,
          spaceBetween: 0,
        },
        1024: {
          slidesPerView: 4,
          spaceBetween: 0,
        },
      },
    });

    swiper.init();
  };

  mql.addEventListener('change', checkBreakpoint);
  checkInitialBreakpoint();
}

// Member & Occupation
const occupationTypeSliders = document.querySelectorAll(
  '.js-occupation-type-swiper'
);

if (occupationTypeSliders.length !== 0) {
  occupationTypeSliders.forEach((slider) => {
    let swiper: Swiper | null = null;

    const sliderElement = slider as HTMLElement;

    const checkBreakpoint = (e: MediaQueryListEvent) => {
      if (!e.matches) {
        initSwiper();
      } else if (swiper) {
        swiper.destroy(false, true);
      }
    };

    const checkInitialBreakpoint = () => {
      if (!mql.matches) {
        initSwiper();
      } else if (swiper) {
        swiper.destroy(false, true);
      }
    };

    const initSwiper = () => {
      swiper = new Swiper(sliderElement, {
        init: false,
        loop: false,
        slidesPerView: 1.3,
        spaceBetween: 10,

        breakpoints: {
          768: {
            slidesPerView: 3,
            spaceBetween: 0,
          },
        },
      });

      swiper.init();
    };

    mql.addEventListener('change', checkBreakpoint);
    checkInitialBreakpoint();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const buttons = document.querySelectorAll<HTMLButtonElement>(
    '.js-diagnosis-button'
  );
  const baseElement = document.querySelector<HTMLElement>('.js-diagnosis-base');
  const diagnosisItems =
    document.querySelectorAll<HTMLElement>('.js-diagnosis-item');

  if (!baseElement || buttons.length === 0 || diagnosisItems.length === 0) {
    return;
  }

  diagnosisItems.forEach((item) => {
    item.style.display = 'none';
  });
  baseElement.style.display = 'block';

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const targetId = button.dataset.target;

      buttons.forEach((btn) => btn.classList.remove('is-active'));
      button.classList.add('is-active');

      baseElement.style.display = 'none';

      diagnosisItems.forEach((item) => {
        item.style.display = 'none';
      });

      if (targetId) {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.style.display = 'block';
        }
      }
    });
  });
});

// culture page
const cultureBlockSwiper = document.querySelectorAll(
  '.js-culture-block-swiper'
);

if (cultureBlockSwiper.length !== 0) {
  cultureBlockSwiper.forEach((slider) => {
    let swiper: Swiper | null = null;

    const sliderElement = slider as HTMLElement;

    const checkBreakpoint = (e: MediaQueryListEvent) => {
      if (!e.matches) {
        initSwiper();
      } else if (swiper) {
        swiper.destroy(false, true);
      }
    };

    const checkInitialBreakpoint = () => {
      if (!mqlLg.matches) {
        initSwiper();
      } else if (swiper) {
        swiper.destroy(false, true);
      }
    };

    const initSwiper = () => {
      swiper = new Swiper(sliderElement, {
        init: false,
        loop: false,
        slidesPerView: 1.5,
        spaceBetween: 24,

        breakpoints: {
          768: {
            slidesPerView: 2.5,
            spaceBetween: 24,
          },
          1024: {
            slidesPerView: 3,
            spaceBetween: 0,
          },
        },
      });

      swiper.init();
    };

    mql.addEventListener('change', checkBreakpoint);
    checkInitialBreakpoint();
  });
}

const stageSwiper = document.querySelector('.js-stage-swiper') as HTMLElement;
if (stageSwiper) {
  let swiper: Swiper | null = null;

  const checkBreakpoint = (e: MediaQueryListEvent) => {
    if (e.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const checkInitialBreakpoint = () => {
    if (mql.matches) {
      initSwiper();
    } else if (swiper) {
      swiper.destroy(false, true);
    }
  };

  const initSwiper = () => {
    swiper = new Swiper(stageSwiper, {
      modules: [Mousewheel],
      init: false,
      loop: false,
      slidesPerView: 1.7,
      spaceBetween: 24,

      mousewheel: {
        forceToAxis: true,
      }
    });

    swiper.init();
  };

  mql.addEventListener('change', checkBreakpoint);
  checkInitialBreakpoint();
}

function adjustHeights(items: NodeListOf<HTMLElement>): void {
  let maxHeight = 0;

  items.forEach((item) => {
    item.style.height = 'auto';
    maxHeight = Math.max(maxHeight, item.clientHeight);
  });

  items.forEach((item) => {
    item.style.height = `${maxHeight}px`;
  });
}

window.addEventListener('resize', () => {
  const textItems = document.querySelectorAll<HTMLElement>(
    '.js-careerup-systems-text'
  );
  if (mqlLg.matches) {
    adjustHeights(textItems);
  }
});

window.addEventListener('load', () => {
  const textItems = document.querySelectorAll<HTMLElement>(
    '.js-careerup-systems-text'
  );
  if (mqlLg.matches) {
    adjustHeights(textItems);
  }
});

const switchPoints = document.querySelectorAll('.js-story-text-swith-point');

if (switchPoints.length !== 0) {
  switchPoints.forEach((point) => {
    const number = point.getAttribute('data-number');
    const targetText = document.getElementById(`story-text-${number}`);

    if (targetText) {
      if (number !== '1') {
        gsap.set(targetText, { opacity: 0, y: 50 });
      }

      if (mqlLg.matches) {
        let end = '+=100%';

        if (point.classList.contains('js-height-double')) {
          end = '+=200%';
        }

        ScrollTrigger.create({
          trigger: point,
          pin: point,
          start: 'center center',
          end: end,
          // markers: true,
          onEnter: () => {
            if (!targetText.classList.contains('js-has-two-text-block')) {
              gsap.to(targetText, {
                opacity: 1,
                duration: 0.5,
                y: 0,
                ease: 'power2.out',
              });
            }
          },
          onLeave: () => {
            if (!targetText.classList.contains('js-has-two-text-block')) {
              gsap.to(targetText, {
                opacity: 0,
                duration: 0.5,
                y: -50,
                ease: 'power2.out',
              });
            }
          },
          onLeaveBack: () => {
            if (!targetText.classList.contains('js-has-two-text-block') && number !== '1') {
              gsap.to(targetText, {
                opacity: 0,
                duration: 0.5,
                y: 50,
                ease: 'power2.out',
              });
            }
          },
          onEnterBack: () => {
            if (!targetText.classList.contains('js-has-two-text-block')) {
              gsap.to(targetText, {
                opacity: 1,
                duration: 0.5,
                y: 0,
                ease: 'power2.out',
              });
            }
          },
        });
      }
    }
  });
}

const storyText7 = document.getElementById('story-text-7');
const switchPoint7 = document.getElementById('switch-point-7')
const storyText8Child = document.getElementById('story-text-8-child');
if (storyText8Child && storyText7 && switchPoint7) {
  gsap.set(storyText7, { opacity: 0, y: 50 });
  gsap.set(storyText8Child, { opacity: 0, y: 50 });

  if (mqlLg.matches) {
    ScrollTrigger.create({
      trigger: switchPoint7,
      start: 'center center',
      end: '+=100%',
      // markers: true,
      onEnter: () => {
        gsap.to(storyText7, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      },
      onLeave: () => {
        gsap.to(storyText7, {
          opacity: 0,
          duration: 0.5,
          y: -50,
          ease: 'power2.out',
        });

        gsap.to(storyText8Child, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      },
      onEnterBack: () => {
        gsap.to(storyText7, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });

        gsap.to(storyText8Child, {
          opacity: 0,
          duration: 0.5,
          y: 50,
          ease: 'power2.out',
        });
      },
      onLeaveBack: () => {
        gsap.to(storyText7, {
          opacity: 0,
          duration: 0.5,
          y: 50,
          ease: 'power2.out',
        });

        gsap.to(storyText8Child, {
          opacity: 0,
          duration: 0.5,
          y: 50,
          ease: 'power2.out',
        });
      },
    });

    ScrollTrigger.create({
      trigger: switchPoint7,
      start: '+=80% center',
      // markers: true,
      onEnter: () => {
        gsap.to(storyText8Child, {
          opacity: 0,
          duration: 0.5,
          y: -50,
          ease: 'power2.out',
        });
      },
      onLeaveBack: () => {
        gsap.to(storyText8Child, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      },
      onEnterBack: () => {
        gsap.to(storyText8Child, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      }
    });
  }
}

const storyText10 = document.getElementById('story-text-10');
const switchPoint10 = document.getElementById('switch-point-10')
const storyText11Child = document.getElementById('story-text-11-child');
if (storyText11Child && storyText10 && switchPoint10) {
  gsap.set(storyText10, { opacity: 0, y: 50 });
  gsap.set(storyText11Child, { opacity: 0, y: 50 });

  if (mqlLg.matches) {
    ScrollTrigger.create({
      trigger: switchPoint10,
      start: 'center center',
      end: '+=100%',
      // markers: true,
      onEnter: () => {
        gsap.to(storyText10, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      },
      onLeave: () => {
        gsap.to(storyText10, {
          opacity: 0,
          duration: 0.5,
          y: -50,
          ease: 'power2.out',
        });

        gsap.to(storyText11Child, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      },
      onEnterBack: () => {
        gsap.to(storyText10, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });

        gsap.to(storyText11Child, {
          opacity: 0,
          duration: 0.5,
          y: 50,
          ease: 'power2.out',
        });
      },
      onLeaveBack: () => {
        gsap.to(storyText10, {
          opacity: 0,
          duration: 0.5,
          y: 50,
          ease: 'power2.out',
        });

        gsap.to(storyText11Child, {
          opacity: 0,
          duration: 0.5,
          y: 50,
          ease: 'power2.out',
        });
      },
    });

    ScrollTrigger.create({
      trigger: switchPoint10,
      start: '+=80% center',
      // markers: true,
      onEnter: () => {
        gsap.to(storyText11Child, {
          opacity: 0,
          duration: 0.5,
          y: -50,
          ease: 'power2.out',
        });
      },
      onLeaveBack: () => {
        gsap.to(storyText11Child, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      },
      onEnterBack: () => {
        gsap.to(storyText11Child, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      }
    });
  }
}

const storyLastTextPoint = document.getElementById('story-last-text-point');
const storyLastText = document.getElementById('story-last-text');

if (storyLastTextPoint) {
  gsap.set(storyLastText, { opacity: 0, y: 50 });

  if (mqlLg.matches) {
    ScrollTrigger.create({
      trigger: storyLastTextPoint,
      start: 'center center',
      end: '+=100%',
      pin: storyLastTextPoint,
      // markers: true,
      onEnter: () => {
        gsap.to(storyLastText, {
          opacity: 1,
          duration: 0.5,
          y: 0,
          ease: 'power2.out',
        });
      },
      onLeaveBack: () => {
        gsap.to(storyLastText, {
          opacity: 0,
          duration: 0.5,
          y: 50,
          ease: 'power2.out',
        });
      },
    });
  }
}

// modal member swiper
const memberModalButtons = document.querySelectorAll('.js-member-modal-button');
const memberModal = document.getElementById('member-modal');

if (memberModalButtons.length !== 0 && memberModal) {
  memberModalButtons.forEach((button) => {
    button.addEventListener('click', () => {
      memberModal.classList.add('is-opened');
    });
  });

  const memberModalCloseButton = document.querySelector(
    '.js-member-modal-close-button'
  );

  if (memberModalCloseButton) {
    memberModalCloseButton.addEventListener('click', () => {
      memberModal.classList.remove('is-opened');
    });
  }
}

const modalMemberSwiper = document.querySelector(
  '.js-member-modal-swiper'
) as HTMLElement;

if (modalMemberSwiper) {
  const swiper = new Swiper(modalMemberSwiper, {
    modules: [Navigation, EffectFade],
    init: false,
    loop: true,
    effect: 'fade',
    slidesPerView: 1,
    spaceBetween: 0,

    navigation: {
      nextEl: '.js-member-modal-swiper-next',
      prevEl: '.js-member-modal-swiper-prev',
    },
  });

  swiper.init();

  memberModalButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const memberId = button.getAttribute('data-member-id');
      swiper.slideTo(Number(memberId) - 1, 0);
    });
  });
}

// policy list in about page
const policyList = document.querySelector('.js-policy-list') as HTMLElement;

if (policyList) {
  const policyItems = Array.from(
    policyList.querySelectorAll('.js-policy-item')
  );

  const updatePolicyListHeight = () => {
    const height = policyItems
      .slice(0, 6)
      .reduce((totalHeight, item) => totalHeight + item.clientHeight, 0);
    policyList.style.height = `${height}px`;
  };

  updatePolicyListHeight();

  window.addEventListener('resize', updatePolicyListHeight);
}
